import axios from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AnimatePresence, animate, motion, useMotionValue, useReducedMotion, useTransform } from 'framer-motion';
import { PiArrowLeftDuotone, PiArrowRightDuotone, PiMouseScrollDuotone, PiNewspaperClippingDuotone } from 'react-icons/pi';

import Noise from 'elements/Noise';
import PageHeading from 'elements/PageHeading';
import Pattern from 'elements/Pattern';
import { BlogItem } from 'pages/BlogPage';

import { getColorConfigByIndex } from 'utils/StyleUtils';

import { useConfig } from 'providers/ConfigProvider';

export default function MainPage(props) {
    return (
        <div className={'bg-white/20 w-full ' + (props.className || '')}>

            <Hero className='relative z-20 rounded-b-3xl shadow-2xl' />

            <ScrollIndicator targetId='featured-content' />

            <Slider id='featured-content' className='relative z-10 -mt-6 rounded-b-3xl shadow-2xl scroll-mt-20' />

            <RecentBlogLister className='-my-6 py-6' />

            <CountyLister className='rounded-t-3xl shadow-2xl' />

        </div>
    );
}

function ScrollIndicator({ targetId }) {
    const reducedMotion = useReducedMotion();

    const scrollToContent = () => {
        document.getElementById(targetId)?.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
    };

    return (
        <div className='relative z-30 h-0 flex justify-center'>
            <button
                type='button'
                onClick={scrollToContent}
                aria-label='Görgess tovább a kiemelt tartalmakhoz'
                className='group absolute top-0 -translate-y-1/2 flex items-center gap-1.5 rounded-full bg-white/90 px-3 py-1.5 text-neutral-600 shadow-lg shadow-black/10 ring-1 ring-black/5 backdrop-blur-md transition-colors hover:bg-white hover:text-neutral-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white'
            >
                <motion.span
                    aria-hidden='true'
                    animate={reducedMotion ? undefined : { y: [0, 2, 0] }}
                    transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
                    className='flex text-neutral-500 transition-colors group-hover:text-neutral-700'
                >
                    <PiMouseScrollDuotone className='size-4' />
                </motion.span>
                <span className='text-[10px] sm:text-[11px] font-medium tracking-wide'>Görgess tovább</span>
            </button>
        </div>
    );
}

function Hero(props) {
    const items = [
        { 'key': 'all', 'label': 'Mutasd az összes diákmunkát!', 'subLabel': 'Mutasd az összes', 'mainLabel': 'diákmunkát!', 'image': 'https://www.furgediak.hu/res/img/splash_page/furgediak.jpg', 'path': '/diakmunka', 'className': 'gradient-green' },
        { 'key': 'mc', 'label': 'Mutasd a Mekis munkákat!', 'subLabel': 'Mutasd a', 'mainLabel': 'Mekis munkákat!', 'image': 'https://www.furgediak.hu/res/img/splash_page/mcdonalds.jpg', 'path': '/meki', 'className': 'gradient-red' },
        { 'key': 'int', 'label': 'Mutasd a gyakornoki munkákat!', 'subLabel': 'Mutasd a', 'mainLabel': 'gyakornoki munkákat!', 'image': 'https://www.furgediak.hu/res/img/splash_page/gyakornoki.jpg', 'path': '/gyakornok', 'className': 'gradient-yellow' },
    ];

    return (
        <div className={'relative w-full flex flex-col items-center px-site animated-gradient overflow-hidden ' + (props.className || '')}>

            <Pattern />

            <div className='w-full max-w-boxed flex flex-col gap-16 2xl:gap-12 items-center pt-16 pb-20 xl:pt-20 xl:pb-24 z-10'>

                <PageHeading title='Válassz, merre böngésznél!' /*subTitle='Válaszd ki a hozzád illő diákmunkát a Fürge Diákkal!'*/ />

                <div className='w-full max-w-[960px] grid grid-cols-1 md:grid-cols-3 gap-16'>
                    {items.map((item) => <HeroItem key={item.label} data={item} />)}
                </div>

            </div>

        </div>
    );
}

function HeroItem(props) {
    return (
        <Link to={props.data.path} className={'w-full flex flex-col items-center rounded-3xl transition duration-500 ring-8 ring-white/20 hover:ring-white/30 active:scale-95 overflow-hidden group ' + (props.className || '')}>

            <div className={'w-full aspect-[320/310] relative rounded-3xl z-10 overflow-hidden shadow-2xl isolate ' + (props.data.className || '')}>
                <img className='block w-full max-w-none scale-[1.01] group-hover:scale-105 transition-transform duration-500 will-change-transform opacity-95' src={props.data.image} alt={props.data.label} />
                <Noise />
            </div>

            <div className='w-full flex-1 flex items-center justify-center gradient-gray rounded-b-3xl -mt-6 pt-6'>

                <div className='flex flex-col py-6 px-4'>
                    <div className='text-white text-center opacity-75 text-sm'>{props.data.subLabel}</div>
                    <div className={'text-2xl sm:text-lg md:text-xl 2xl:text-2xl text-white text-center font-bold font-display capitalize group-hover:scale-105 transition duration-500'}>{props.data.mainLabel}</div>
                </div>

            </div>

        </Link>
    );
}

const SLIDE_INTERVAL = 8000;
const SWIPE_OFFSET = 100;
const SWIPE_VELOCITY = 500;
const SLIDE_IMAGE_SCALE = 1.12;
const getSlideImageUrl = (photoKey) => global.backingsEndpoint + 'photoRepository/getImageBySize?size=ORIGINAL&key=' + photoKey;

function Slider(props) {
    const [list, setList] = useState([]);
    const [activeIndex, setActiveIndex] = useState(0);
    const [hovered, setHovered] = useState(false);
    const [focusWithin, setFocusWithin] = useState(false);
    const [interacting, setInteracting] = useState(false);
    const [pageVisible, setPageVisible] = useState(true);
    const [visibilityRevision, setVisibilityRevision] = useState(0);
    const reducedMotion = useReducedMotion();
    const swipeOffset = useMotionValue(0);
    const leftHintOpacity = useTransform(swipeOffset, [-SWIPE_OFFSET, 0], [1, 0]);
    const leftHintScale = useTransform(swipeOffset, [-SWIPE_OFFSET, 0], [1, 0.8]);
    const rightHintOpacity = useTransform(swipeOffset, [0, SWIPE_OFFSET], [0, 1]);
    const rightHintScale = useTransform(swipeOffset, [0, SWIPE_OFFSET], [0.8, 1]);
    const timerPaused = hovered || focusWithin || interacting || !pageVisible;

    const paginate = useCallback((newDirection) => {
        if (list.length < 2) return;
        const nextIndex = (activeIndex + newDirection + list.length) % list.length;
        setActiveIndex(nextIndex);
    }, [activeIndex, list.length]);

    useEffect(() => {
        if (timerPaused || list.length < 2) return undefined;
        const timer = setTimeout(() => paginate(1), SLIDE_INTERVAL);
        return () => clearTimeout(timer);
    }, [activeIndex, list.length, paginate, timerPaused, visibilityRevision]);

    useEffect(() => {
        const handleVisibilityChange = () => {
            const visible = document.visibilityState === 'visible';
            setPageVisible(visible);
            setInteracting(false);
            swipeOffset.set(0);
            if (visible) setVisibilityRevision((revision) => revision + 1);
        };

        setPageVisible(document.visibilityState === 'visible');
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [swipeOffset]);

    useEffect(() => {
        const url = global.backingsEndpoint + 'slideshowControl/getSlideshowImageKeys';
        axios.post(url, { slideshowId: 2 }).then((response) => setList(response.data?.slideImage || [])).catch((error) => console.log(error));
    }, []);

    const selectSlide = (index) => {
        if (index === activeIndex) return;
        setActiveIndex(index);
    };

    const activeSlide = list[activeIndex];

    return (
        <section
            id={props.id}
            aria-roledescription='carousel'
            aria-label='Kiemelt tartalmak'
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onFocusCapture={() => setFocusWithin(true)}
            onBlurCapture={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setFocusWithin(false); }}
            className={'relative w-full h-[clamp(480px,78svh,680px)] md:h-auto md:aspect-[16/9] lg:aspect-[16/8] 2xl:aspect-[16/7] rounded-b-3xl gradient-gray overflow-hidden isolate ' + (props.className || '')}
        >
            {list.map((item, index) => <Slide key={item.id || item.photoKey || item.title} data={item} active={activeIndex === index} swipeOffset={swipeOffset} onSwipe={paginate} onInteractionChange={setInteracting} />)}

            <div className='pointer-events-none absolute inset-0 z-[5]'><Noise /></div>

            <div className='pointer-events-none absolute inset-0 z-10 rounded-b-3xl bg-gradient-to-t from-black/80 via-black/45 to-black/15 md:bg-gradient-to-r md:from-black/70 md:via-black/35 md:to-black/10' />

            <motion.div style={{ opacity: leftHintOpacity, scale: leftHintScale }} className='pointer-events-none absolute left-4 sm:left-8 top-1/2 -translate-y-1/2 z-30 size-12 sm:size-14 flex items-center justify-center rounded-2xl bg-black/20 text-white shadow-xl backdrop-blur-md'>
                <PiArrowLeftDuotone className='size-6 sm:size-7' />
            </motion.div>
            <motion.div style={{ opacity: rightHintOpacity, scale: rightHintScale }} className='pointer-events-none absolute right-4 sm:right-8 top-1/2 -translate-y-1/2 z-30 size-12 sm:size-14 flex items-center justify-center rounded-2xl bg-black/20 text-white shadow-xl backdrop-blur-md'>
                <PiArrowRightDuotone className='size-6 sm:size-7' />
            </motion.div>

            <AnimatePresence initial={false} mode='wait'>
                {activeSlide && <SlideContent key={`${activeSlide.id || activeSlide.photoKey || activeSlide.title}-${visibilityRevision}`} data={activeSlide} index={activeIndex} reducedMotion={reducedMotion} />}
            </AnimatePresence>

            {list.length > 1 && <>
                <div className='absolute left-1/2 bottom-6 sm:bottom-8 -translate-x-1/2 z-30 flex items-center gap-2 rounded-full bg-black/20 p-2 backdrop-blur-md shadow-xl'>
                    {list.map((item, index) => <SlideDot key={item.id || item.photoKey || item.title} index={index} active={activeIndex === index} onClick={() => selectSlide(index)} />)}
                </div>
            </>}

            {!activeSlide && <div className='absolute inset-0 bg-white/10' />}
        </section>
    );
}

function Slide(props) {
    const handlePanEnd = (_, { offset, velocity }) => {
        const swipeDirection = offset.x <= -SWIPE_OFFSET || velocity.x <= -SWIPE_VELOCITY
            ? 1
            : offset.x >= SWIPE_OFFSET || velocity.x >= SWIPE_VELOCITY ? -1 : 0;

        animate(props.swipeOffset, 0, { duration: 0.18, ease: 'easeOut' });
        props.onInteractionChange(false);
        if (swipeDirection) props.onSwipe(swipeDirection);
    };

    return (
        <motion.div
            aria-hidden={!props.active}
            onPanStart={() => { props.swipeOffset.set(0); props.onInteractionChange(true); }}
            onPan={(_, { offset }) => props.swipeOffset.set(offset.x)}
            onPanEnd={handlePanEnd}
            className={'absolute inset-0 rounded-b-3xl overflow-hidden touch-pan-y select-none ' + (props.active ? 'z-[2]' : 'z-[1] pointer-events-none')}
        >
            <img
                src={getSlideImageUrl(props.data.photoKey)}
                alt=''
                draggable={false}
                loading='eager'
                decoding='async'
                className={'absolute inset-0 size-full object-cover transform-gpu transition-[opacity,transform] duration-700 ease-out ' + (props.active ? 'opacity-100' : 'opacity-0')}
                style={{ transform: props.active ? 'translateZ(0) scale(1)' : `translateZ(0) scale(${SLIDE_IMAGE_SCALE})`, willChange: 'opacity, transform', backfaceVisibility: 'hidden' }}
            />
        </motion.div>
    );
}

function SlideContent({ data, index, reducedMotion }) {
    const navigate = useNavigate();

    const handleNavigate = () => {
        if (!data.url) return;
        const url = new URL(data.url, window.location.origin);
        navigate(url.pathname + url.search + url.hash);
    };

    return (
        <motion.div initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 32 }} animate={{ opacity: 1, y: 0 }} exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -20 }} transition={{ duration: 0.5, ease: 'easeOut' }} className='pointer-events-none absolute inset-0 z-20 flex items-center justify-center px-site'>
            <div className='w-full max-w-4xl flex flex-col gap-3 sm:gap-4 items-center justify-center text-white text-center px-8 sm:px-14'>
                <h2 className='font-display font-black text-4xl sm:text-5xl lg:text-6xl 2xl:text-7xl leading-tight text-balance'>{data.title}</h2>
                <p className='max-w-3xl text-sm sm:text-base md:text-lg xl:text-xl 2xl:text-2xl leading-relaxed text-white/85'>{data.subTitle}</p>
                {data.text && <motion.button type='button' onClick={handleNavigate} whileTap={{ scale: 0.96 }} className={'pointer-events-auto button mt-4 sm:mt-6 md:mt-8 ' + getColorConfigByIndex(index, 'gradient')}>
                    {data.text}<PiArrowRightDuotone />
                </motion.button>}
            </div>
        </motion.div>
    );
}

function SlideDot(props) {
    return (
        <button type='button' aria-label={`${props.index + 1}. dia`} aria-current={props.active ? 'true' : undefined} onClick={props.onClick} className={'relative h-2.5 overflow-hidden rounded-full transition-[width,opacity] duration-500 ' + (props.active ? 'w-10 opacity-100' : 'w-2.5 bg-white/40 opacity-70 hover:opacity-100')}>
            {props.active && <span className={'absolute inset-0 ' + getColorConfigByIndex(props.index, 'gradient')} />}
        </button>
    );
}

function RecentBlogLister(props) {
    const [list, setList] = useState([]);

    useEffect(() => {
        const url = global.backingsEndpoint + 'landingPageControl/getBlogListByKeys'
        const body = { 'firstResult': 0, 'maxResults': 3, 'keys': null }
        axios.post(url, body).then(function (response) {
            if (response.data)
                setList(response.data);
        }).catch(function (error) { console.log(error); });
    }, []);

    return (
        <div className={'relative w-full flex flex-col items-center px-site bg-black/40 ' + (props.className || '')} >

            <Pattern />

            <div className='w-full max-w-boxed flex flex-col justify-center items-center gap-8 2xl:gap-12 py-12 2xl:py-24 z-10 '>

                <div className='w-full flex flex-col md:flex-row gap-8 items-center'>
                    <div className='flex-1 flex flex-col gap-2 text-white text-center md:text-left'>
                        <div className='font-display font-black text-4xl sm:text-5xl lg:text-5xl xl:text-6xl'>Legfrissebb blogbejegyzéseink</div>
                        <div className=''>Böngéssz a legfrissebb tartalmaink között a diákmunka világából</div>
                    </div>
                    <div className='hidden md:block'>
                        <Link to='/blog' className='button gradient-yellow'><PiNewspaperClippingDuotone />Tovább a Blogra</Link>
                    </div>
                </div>

                <div className='w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 sm:gap-8'>
                    {list.map((item, index) => <BlogItem className={index >= 3 ? 'hidden md:inline-block' : 'inline-block'} key={item.id} data={item} index={index} />)}
                </div>

                <div className='w-full md:hidden -mt-10'>
                    <Link to='/blog' className='w-full button gradient-yellow'><PiNewspaperClippingDuotone />Tovább a Blogra</Link>
                </div>

            </div>

        </div>
    );
}

function CountyLister(props) {
    const { comboData } = useConfig();

    return (
        <div className={'relative w-full bg-black px-site py-10 md:py-16 2xl:py-32 flex flex-col items-center shadow-2xl overflow-hidden ' + (props.className || '')}>

            <div className='absolute top-0 left-0 w-full h-full bg-cover bg-center bg-no-repeat opacity-20 sm:opacity-40' style={{ backgroundImage: 'url(/assets/images/background/standing-at-the-wall.jpg)' }} />

            <div className='w-full max-w-boxed flex flex-col gap-8 md:gap-12 xl:gap-20 z-10'>

                <h2 className='font-display font-black text-white text-2xl sm:text-4xl md:text-5xl xl:text-6xl 2xl:text-7xl text-center'>Diákmunka ajánlatok télen-nyáron a Fürge&nbsp;Diák weboldalán!</h2>

                <div className='flex flex-col gap-8 sm:bg-black/10 rounded-3xl sm:backdrop-blur-lg sm:shadow-2xl sm:border border-white/10 p-0 sm:p-8 md:p-16'>

                    <div className='flex flex-col gap-4 text-white text-center sm:text-left'>
                        <div className='font-display text-sm sm:text-2xl md:text-3xl font-bold'>Mennyi szabad időd van diákmunkát vállalni? Folyamatosan dolgoznál, vagy csak alkalmi diákmunka érdekelne? Esetleg kellene diákmunka nyárra?</div>
                        <div className='text-xs sm:text-sm leading-relaxed'>Nézz körül a diákmunkák között, és találd meg azt, ami megfelel az igényeidnek! Ha csak a nyári diákmunka ajánlatok érdekelnek, akkor is jó helyen jársz a Fürge Diák Iskolaszövetkezetnél. A nyár kiemelt időszak partnereink életében, ilyenkor sokkal több a nyári munka diákoknak is.</div>
                        <div className='font-display text-sm sm:text-2xl md:text-3xl font-bold'>Legyen saját pénzed, amiből megveheted a számodra legfontosabb dolgokat! Válassz diák munka ajánlataink közül a nyári hónapokban is!</div>
                        <div className='text-xs sm:text-sm leading-relaxed'>Keress akár alkalmi diákmunkát vagy folyamatosat, ránk számíthatsz! 😉 Válassz diákmeló lehetőségeink közül az ország számos pontján!</div>
                    </div>

                    {comboData && <div className='w-full flex gap-2.5 flex-wrap justify-center'>
                        {comboData.county.map((item) => <CountyItem key={item.id} data={item} />)}
                    </div>}

                </div>

            </div>

        </div>
    );
}

function CountyItem(props) {
    return (
        <Link to={`/diakmunka?countyId=${props.data.id}`} className='bg-white/95 rounded-full px-2 sm:px-3 py-1 shadow-2xl cursor-pointer hover:bg-white/100 transition duration-300'>
            <div className='text-[9px] sm:text-xs leading-none'>
                #Diákmunka <span className='font-semibold'>{props.data.name}</span>
            </div>
        </Link>
    );
}
